import clsx from 'clsx';
import { memo } from 'react';
import { SlotText } from 'slot-text/react';

import type { SharedIconProps } from '../../types/SharedIconProps';
import type { Status } from '../../types/Status';
import type { ChipColor } from '../Chip/Chip';

import { useNumberFormat } from '../../context/NumberFormat/NumberFormat';
import { Icon } from '../Icon/Icon';
import { resolveCountBadgeCountText } from './utils';
import 'slot-text/style.css';

import './CountBadge.css';

/** EDS-internal only — never set from src/**, only passed sibling-to-sibling within packages/eds. */
type CountBadgePrivateProps = {
  /** Color variant. */
  color?: ChipColor;
  /** Seats the badge against a host's trailing edge: square corners, symmetric
   * padding, and the host's full height. Set by hosts that render the badge as
   * their last element (e.g. `Chip`). */
  mode?: 'attached';
};

export type CountBadgeProps = {
  /** Raw numeric count used for render gating and overflow threshold logic. */
  count: number;
  /** Optional leading icon. */
  icon?: SharedIconProps;
  /** Maximum count before the value is treated as overflow. Uncapped by
   * default — the full count renders. */
  maxCount?: number;
  /** Overall badge size — the text stays the same, size trims its height.
   * @default 'm' */
  size?: 'm' | 's';
  /** Foreground/background tone. @default 'neutral' */
  status?: Status;
  __private?: CountBadgePrivateProps;
};

export const CountBadge = memo(
  ({
    count,
    icon,
    maxCount,
    size = 'm',
    status = 'neutral',
    __private,
  }: CountBadgeProps) => {
    const formatNumber = useNumberFormat();

    if (count <= 0) {
      return null;
    }

    const isOverflow = maxCount !== undefined && count > maxCount;
    const countText = resolveCountBadgeCountText(count, formatNumber, maxCount);

    return (
      <div
        className={clsx(
          'eds-count-badge',
          `eds-styles--status-${status}`,
          size === 's' && 'eds-count-badge--size-s',
          __private?.mode === 'attached' && 'eds-count-badge--attached',
          icon && 'eds-count-badge--has-icon',
          isOverflow && 'eds-count-badge--overflow',
          __private?.color && `eds-count-badge--color-${__private.color}`,
        )}
      >
        {icon && (
          <Icon
            filled={icon.filled}
            icon={icon.key}
            label={icon.label}
            size="xs"
          />
        )}
        <SlotText
          text={countText}
          className="eds-text eds-text--tone-auto eds-text--variant-body-semi-s eds-count-badge--text"
          options={{ rollBy: 'character' }}
        />
      </div>
    );
  },
);

CountBadge.displayName = 'CountBadge';
